/**
 * Nomination Redux Slice
 * 
 * Manages state for nomination functionality in the 360-degree feedback system.
 * Handles nomination form data, submission states, and error management for
 * nominating colleagues for performance reviews.
 */

import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

/**
 * Nomination Form Data Interface
 * 
 * Represents the data structure for a single nomination entry
 */
interface NominationFormData {
  nomineeId: string;    // ID of the person being nominated
  relationship: string; // Relationship type (peer, superior, subordinate, etc.)
  reason: string;       // Reason/justification for the nomination
}

/**
 * Nomination State Interface
 * 
 * Defines the complete state structure for nomination functionality
 */
interface NominationState {
  nominations: NominationFormData[];     // Array of submitted nominations
  currentNomination: NominationFormData; // Form data being currently edited
  isSubmitting: boolean;                 // Form submission state
  error: string | null;                  // Error message if any
}

/**
 * Initial state for nomination functionality
 * 
 * Sets up default empty state for nominations
 */
const initialState: NominationState = {
  nominations: [],
  currentNomination: {
    nomineeId: '',
    relationship: '',
    reason: '',
  },
  isSubmitting: false,
  error: null,
};

/**
 * Nomination slice with reducers for managing nomination data
 */
const nominationSlice = createSlice({
  name: 'nomination',
  initialState,
  reducers: {
    /**
     * Update a field in the current nomination form
     * 
     * @param action - Contains field name and new value
     */
    updateCurrentNomination: (state, action: PayloadAction<{ field: keyof NominationFormData; value: string }>) => {
      state.currentNomination[action.payload.field] = action.payload.value;
    },
    
    /**
     * Add current nomination to the list and reset form
     */
    addNomination: (state) => {
      state.nominations.push({ ...state.currentNomination });
      // Reset current nomination form after adding
      state.currentNomination = { nomineeId: '', relationship: '', reason: '' };
    },
    
    /**
     * Start nomination submission process
     */
    submitNominationStart: (state) => {
      state.isSubmitting = true;
      state.error = null;
    },
    
    /**
     * Handle successful nomination submission
     */
    submitNominationSuccess: (state) => {
      state.isSubmitting = false;
      state.error = null;
    },
    
    /**
     * Handle failed nomination submission
     * 
     * @param action - Contains error message
     */
    submitNominationFailure: (state, action: PayloadAction<string>) => {
      state.isSubmitting = false;
      state.error = action.payload;
    },
    
    /**
     * Clear any existing nomination error messages
     */
    clearNominationError: (state) => {
      state.error = null;
    },
  },
});

// Export action creators for use in components
export const {
  updateCurrentNomination,
  addNomination,
  submitNominationStart,
  submitNominationSuccess,
  submitNominationFailure,
  clearNominationError,
} = nominationSlice.actions;

// Export reducer for store configuration
export default nominationSlice.reducer;
